// Chat/shared/WatchAgainChip.tsx
//
// A `[[watch 0:38-0:57]]` token in a companion answer (ORA-1055): a chip that
// seeks the course video on screen to the start of the range and plays it.
// With no course video on screen there is nothing to seek, so the range is
// plain text.

import { Play } from '@phosphor-icons/react';
import {
  seekActiveCourseVideo,
  useHasActiveCourseVideo,
} from '@/chatContext/courseVideoControlStore';
import { formatWatchRange, type WatchRange } from './watchToken';

interface WatchAgainChipProps {
  range: WatchRange;
}

const WatchAgainChip = ({ range }: WatchAgainChipProps) => {
  const hasCourseVideo = useHasActiveCourseVideo();
  const label = formatWatchRange(range);

  if (!hasCourseVideo) return <>{label}</>;

  // The model writes "Watch again:" before the token, so the chip shows the
  // range alone; its accessible name carries the whole action.
  return (
    <button
      type="button"
      data-testid="chat-watch-again"
      aria-label={`Watch again ${label}`}
      title={`Watch again ${label}`}
      // Read at click time, never from a closure: the answer's parsed blocks
      // are memoised, and the video on screen may have changed since.
      onClick={() => seekActiveCourseVideo(range.startSec)}
      className="mx-0.5 inline-flex cursor-pointer items-center gap-1 rounded-full border border-border-subtle bg-surface-muted px-2 py-0.5 align-baseline text-xs font-medium text-fg-primary tabular-nums transition-colors hover:bg-surface-raised focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <Play weight="fill" aria-hidden="true" className="size-3" />
      {label}
    </button>
  );
};

export default WatchAgainChip;
